import {
    always,
    compose,
    cond,
    filter,
    ifElse,
    isEmpty,
    join,
    map,
    T,
    toPairs,
    __,
} from 'ramda';
import { sharePointHelpers } from '@qdabra/forms-shared/sharePoint';
import { getAndSanitizeInternalName, isAttachmentField, isExpandField, isPersonField } from './list.rest.helpers';
import { partitionSelectAndExpand } from './list.rest.buildUrl.partition';

const { getApiPathPrefix, listItemsUrl, restString } = sharePointHelpers;

const delimitFieldNames = compose(
    join(','),
    map(getAndSanitizeInternalName),
);

// $expand

// [ListAdapterField] -> String
const buildExpandSegment = compose(
    ifElse(
        isEmpty,
        always(null),
        (fs) => `$expand=${delimitFieldNames(fs)}`,
    ),
    filter(isExpandField),
);


// $select

const selectProperties = (properties) => compose(
    join(','),
    (name) => map((p) => `${name}/${p}`, properties),
    getAndSanitizeInternalName,
);

const personFieldSelect =
    selectProperties(['Title', 'Name']);

const attachmentSelect =
    selectProperties(['ServerRelativeUrl', 'FileName']);

const buildFieldSelect = cond([
    [isPersonField, personFieldSelect],
    [isAttachmentField, attachmentSelect],
    [T, getAndSanitizeInternalName],
]);

const buildSelectSegment = compose(
    (fs) => `$select=${fs.join(',')}`,
    map(buildFieldSelect),
);


// $filter

const buildFieldFilter = ([name, value]) => `${name} eq ${restString(value)}`

const buildFilterSegment = compose(
    ifElse(
        isEmpty,
        always(null),
        (fs) => `$filter=${fs.join(' and ')}`,
    ),
    map(buildFieldFilter),
    toPairs,
);


// $orderBy
const buildOrderBy = (sortBy, sortAsc) =>
    sortBy
        ? `$orderBy=${sortBy} ${sortAsc ? 'asc' : 'desc'}`
        : null;

// $top
const TOP_SEGMENT = '$top=5000';

const buildListQueryUrl = (fields, sortBy, sortAsc, useName, listIdentifier, filterValues, { omitSelectAndExpand } = {}) => {
    if (isEmpty(fields)) {
        throw new Error('No fields to query');
    }

    const segments = [
        omitSelectAndExpand ? null : buildExpandSegment(fields),
        omitSelectAndExpand ? null : buildSelectSegment(fields),
        buildFilterSegment(filterValues),
        buildOrderBy(sortBy, sortAsc),
        TOP_SEGMENT,
    ].filter(Boolean);

    return listItemsUrl(useName, listIdentifier) + '?' + segments.join('&');
};

export const buildPartitionedListQueryUrls = (siteUrl, fields, sortBy, sortAsc, useName, listIdentifier, filterValues, maxLength) => {
    const apiPrefix = getApiPathPrefix(siteUrl);
    const listQueryPrefix = buildListQueryUrl(fields, sortBy, sortAsc, useName, listIdentifier, filterValues, { omitSelectAndExpand: true });

    const fullPrefix = apiPrefix + listQueryPrefix;

    const segments = partitionSelectAndExpand(
        fields,
        maxLength - fullPrefix.length,
    );

    return segments.map(({ select, expand }) => fullPrefix + select + expand)
};


export default buildListQueryUrl;